import { useEffect, useId, useMemo, useRef, useState } from "react";
import { formatText } from "../i18n/texts";
import { ChoiceMenu, type ChoiceOption } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import type { Group, Interval, Member, Page, Schedule, Zone } from "./teamApi";
import { useApi, useOperation } from "./teamHooks";
import { hourOf, minuteOf, sameDate, type DateOnly } from "./teamTime";
import { members as membersText } from "./teamText";
import { Avatar, TeamEmpty, TeamLoader, TeamSearch, TeamSwitch, useT, useTeam, type SearchHandle } from "./TeamUi";

// Selectores de Equipo con el mismo HTML que el portal actual: hora (TeamTimeSelect, C36), zona (TeamTimezoneSelect, C08),
// fecha (TeamDateSelect), días y horas (TeamWeekEditor), horario compartido (TeamSchedulePicker) y selector de varios
// (TeamRecordSelector).

const icons = {
  searchIcon: <ChatIcon name="search" />,
  clearIcon: <ChatIcon name="x" />,
  chevron: <ChatIcon name="chevron-down" />,
  check: <ChatIcon name="check" />,
};

const timeOptions = (locale: string): ChoiceOption[] =>
  Array.from({ length: 96 }, (_, step) => {
    const hour = Math.trunc(step / 4),
      minute = (step % 4) * 15;
    const value = hourOf(hour * 60 + minute);
    // es-PE escribe 24 h ("09:15"); en-US, 12 h ("9:15 AM"). El valor guardado es siempre HH:mm.
    const label = locale.startsWith("en")
      ? `${hour % 12 === 0 ? 12 : hour % 12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`
      : value;
    return { value, label };
  });

/** «9:1» deja 9:15: compara el comienzo de la hora (con o sin el cero) y de su etiqueta. */
const startsWithTime = (option: ChoiceOption, typed: string) => {
  const value = typed.trim();
  return (
    option.value.startsWith(value) ||
    option.value.replace(/^0+/, "").startsWith(value) ||
    option.label.toLowerCase().startsWith(value.toLowerCase())
  );
};

export function TimeSelect({
  id,
  label,
  value,
  onChange,
  disabled,
}: {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const ui = useTeam();
  const T = useT();
  const generated = `tm-time-${useId().replace(/:/g, "")}`;
  const options = useMemo(() => timeOptions(ui.locale), [ui.locale]);
  return (
    <div className="tm-time" data-part="hora">
      <ChoiceMenu
        id={id ?? generated}
        label={label}
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled}
        match={startsWithTime}
        searchLabel={T("CFG1_SEARCH")}
        clearSearchLabel={T("CFG1_R1_CLEAR_SEARCH")}
        {...icons}
      />
    </div>
  );
}

let zonesShared: ChoiceOption[] | null = null;
/** Las zonas se piden al abrir la pantalla: el portal actual las tiene en el servidor y su menú abre ya con todas. */
export function prefetchZones(api: { zones: () => Promise<Zone[]> }): void {
  if (zonesShared) return;
  api.zones().then(
    (zones) => {
      zonesShared = zones.map((zone) => ({ value: zone.id, label: zone.label }));
    },
    () => undefined,
  );
}
export function TimezoneSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const api = useApi();
  const T = useT();
  const id = `tm-zone-${useId().replace(/:/g, "")}`;
  const [options, setOptions] = useState<ChoiceOption[]>(zonesShared ?? []);
  useEffect(() => {
    if (zonesShared) return;
    api.zones().then(
      (zones: Zone[]) => {
        zonesShared = zones.map((zone) => ({ value: zone.id, label: zone.label }));
        setOptions(zonesShared);
      },
      () => undefined,
    );
  }, [api]);
  return (
    <div className="tm-field" data-part="zona">
      <span id={`${id}-label`}>{label}</span>
      <ChoiceMenu
        id={id}
        label={label}
        value={value}
        onChange={onChange}
        options={options}
        searchLabel={T("CFG1_SEARCH")}
        clearSearchLabel={T("CFG1_R1_CLEAR_SEARCH")}
        {...icons}
      />
    </div>
  );
}

// Nombres de día más cortos de .NET (GetShortestDayName), de domingo a sábado.
const shortestDays: Record<string, string[]> = {
  es: ["D", "L", "M", "M", "J", "V", "S"],
  en: ["S", "M", "T", "W", "T", "F", "S"],
};
const monthName = (locale: string, month: number) =>
  new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2026, month - 1, 1)));
/** .NET «D»: «sábado, 24 de octubre de 2026» o «Saturday, October 24, 2026». */
export const longDate = (locale: string, date: DateOnly) =>
  new Intl.DateTimeFormat(locale, { dateStyle: "full", timeZone: "UTC" }).format(
    new Date(Date.UTC(date.year, date.month - 1, date.day)),
  );
/** .NET «d». */
export const shortDateOnly = (locale: string, date: DateOnly) =>
  locale.startsWith("en")
    ? `${date.month}/${date.day}/${date.year}`
    : `${String(date.day).padStart(2, "0")}/${String(date.month).padStart(2, "0")}/${date.year}`;

/** Fecha propia (sin control nativo): botón con la fecha y un mes con ‹ ›; Esc o elegir un día lo cierran. */
export function DateSelect({
  label,
  value,
  onChange,
  today,
}: {
  label: string;
  value: DateOnly;
  onChange: (value: DateOnly) => void;
  today: DateOnly;
}) {
  const ui = useTeam();
  const T = useT();
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState({ year: value.year, month: value.month });
  const language = ui.locale.startsWith("en") ? "en" : "es";
  const days = new Date(Date.UTC(month.year, month.month, 0)).getUTCDate();
  const offset = (new Date(Date.UTC(month.year, month.month - 1, 1)).getUTCDay() + 6) % 7;
  const move = (delta: number) =>
    setMonth((current) => {
      const next = new Date(Date.UTC(current.year, current.month - 1 + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1 };
    });
  return (
    <div
      className="tm-date"
      onKeyDown={(event) => {
        if (event.key === "Escape") setOpen(false);
      }}
      data-part="fecha"
    >
      <button
        type="button"
        className="tm-date-trigger"
        aria-haspopup="dialog"
        aria-expanded={open ? "true" : "false"}
        aria-label={`${label}: ${longDate(ui.locale, value)}`}
        onClick={() => {
          setOpen(!open);
          setMonth({ year: value.year, month: value.month });
        }}
      >
        <span>{shortDateOnly(ui.locale, value)}</span>
        <ChatIcon name="calendar-clock" />
      </button>
      {open ? (
        <div className="tm-date-panel" role="dialog" aria-label={label}>
          <header>
            <button
              type="button"
              className="tm-icon-btn"
              aria-label={T("CFG1_PREVIOUS")}
              title={T("CFG1_PREVIOUS")}
              onClick={() => move(-1)}
            >
              <ChatIcon name="chevron-left" />
            </button>
            <span>{`${monthName(ui.locale, month.month)} ${month.year}`}</span>
            <button
              type="button"
              className="tm-icon-btn"
              aria-label={T("CFG1_NEXT")}
              title={T("CFG1_NEXT")}
              onClick={() => move(1)}
            >
              <ChatIcon name="chevron-right" />
            </button>
          </header>
          <div className="tm-date-grid">
            {Array.from({ length: 7 }, (_, dow) => (
              <span key={`d${dow}`} aria-hidden="true">
                {shortestDays[language]![(dow + 1) % 7]}
              </span>
            ))}
            {Array.from({ length: offset }, (_, blank) => (
              <span key={`b${blank}`}></span>
            ))}
            {Array.from({ length: days }, (_, index) => {
              const date = { year: month.year, month: month.month, day: index + 1 };
              return (
                <button
                  key={index}
                  type="button"
                  className={sameDate(date, today) ? "today" : undefined}
                  aria-pressed={sameDate(date, value) ? "true" : "false"}
                  aria-label={longDate(ui.locale, date)}
                  onClick={() => {
                    setOpen(false);
                    onChange(date);
                  }}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- Días y horas (TeamWeekEditor) ----

export type WeekDay = { active: boolean; start: string; end: string };
export type Week = Record<number, WeekDay>;
const order = [1, 2, 3, 4, 5, 6, 0];
export const dayKeys = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

/** Días a partir de los intervalos guardados; sin intervalos, lunes a viernes de 09:00 a 18:00. */
export function weekFrom(intervals: Interval[] | null | undefined): Week {
  const week: Week = {};
  for (let day = 0; day < 7; day++)
    week[day] = { active: !(intervals && intervals.length > 0) && day >= 1 && day <= 5, start: "09:00", end: "18:00" };
  for (const interval of intervals ?? [])
    week[interval.day] = { active: true, start: hourOf(interval.startMinute), end: hourOf(interval.endMinute % 1440) };
  return week;
}
/** Intervalos para guardar: uno por día activo; 00:00–00:00 es el día completo (0–1440). */
export function weekIntervals(week: Week): Interval[] {
  return Object.entries(week)
    .filter(([, day]) => day.active)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([day, value]) => {
      const start = minuteOf(value.start),
        end = minuteOf(value.end);
      return { day: Number(day), startMinute: start, endMinute: end === 0 && start === 0 ? 1440 : end };
    });
}
export const weekValid = (week: Week) =>
  Object.values(week).some((day) => day.active) &&
  Object.values(week)
    .filter((day) => day.active)
    .every((day) => day.start !== day.end || day.start === "00:00");
/** Cada día tiene a lo sumo un intervalo: el horario cabe en este editor. */
export const weekFits = (intervals: Interval[]) =>
  new Set(intervals.map((interval) => interval.day)).size === intervals.length;

export function WeekEditor({
  week,
  onChange,
  disabled,
}: {
  week: Week;
  onChange: (week: Week) => void;
  disabled?: boolean;
}) {
  const T = useT();
  const set = (day: number, change: Partial<WeekDay>) => onChange({ ...week, [day]: { ...week[day]!, ...change } });
  return (
    <div className="tm-day-rows" role="group" aria-label={T("CFG1_R1_DAYS")}>
      {order.map((day) => {
        const row = week[day]!;
        const name = T("CFG1_" + dayKeys[day]);
        return (
          <div className="tm-day-row" key={day}>
            <TeamSwitch
              title={name}
              line={row.active ? null : T("CFG1_R1_DAY_OFF")}
              checked={row.active}
              disabled={disabled}
              onChange={(active) => set(day, { active })}
            />
            {row.active ? (
              <>
                <TimeSelect
                  label={`${T("CFG1_START")} · ${name}`}
                  value={row.start}
                  disabled={disabled}
                  onChange={(start) => set(day, { start })}
                />
                <TimeSelect
                  label={`${T("CFG1_END")} · ${name}`}
                  value={row.end}
                  disabled={disabled}
                  onChange={(end) => set(day, { end })}
                />
                {row.end === row.start && row.start === "00:00" ? (
                  <small className="tm-hint">{T("CFG1_FULL_DAY")}</small>
                ) : row.end < row.start ? (
                  <small className="tm-hint">{T("CFG1_NEXT_DAY")}</small>
                ) : row.end === row.start ? (
                  <small className="tm-field-error">{T("CFG1_R1_SAME_TIME")}</small>
                ) : null}
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ---- Horario compartido (TeamSchedulePicker) ----

export function SchedulePicker({
  label,
  value,
  onChange,
  emptyLabelKey = "CFG1_NO_SCHEDULE",
  help,
  unset,
  choiceError,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  emptyLabelKey?: string;
  help?: string;
  unset?: boolean;
  choiceError?: string | null;
}) {
  const T = useT();
  const api = useApi();
  const { run, accept } = useOperation();
  const [items, setItems] = useState<Schedule[] | null>(null);
  useEffect(() => {
    void run(async () => {
      const result = await api.get<Page<Schedule>>("schedules?search=&page=1&size=100");
      if (accept(result)) setItems(result.value?.items ?? []);
    });
  }, [api, run, accept]);
  const options: ChoiceOption[] = [
    { value: "", label: T(emptyLabelKey) },
    ...(items ?? []).map((schedule) => ({
      value: String(schedule.scheduleId),
      label: schedule.active ? schedule.name : `${schedule.name} · ${T("CFG1_INACTIVE")}`,
      disabled: !schedule.active && schedule.scheduleId !== value,
    })),
  ];
  return (
    <div className="tm-field" data-part="horario-compartido">
      <span>{label}</span>
      <ChoiceMenu
        label={label}
        value={unset ? "?" : value == null ? "" : String(value)}
        onChange={(next) => onChange(/^\d+$/.test(next) ? Number(next) : null)}
        options={options}
        searchLabel={T("CFG1_SEARCH")}
        clearSearchLabel={T("CFG1_R1_CLEAR_SEARCH")}
        placeholder={T("CFG1_R1_CHOOSE_SCHEDULE_PLACEHOLDER")}
        invalid={!!choiceError}
        {...icons}
      />
      {items !== null && items.length === 0 ? (
        <small>
          {T("CFG1_R1_NO_SCHEDULES")}{" "}
          <a className="tm-link" href="/portal/chat/configuracion/horarios">
            {T("CFG1_NEW_SCHEDULE")}
          </a>
        </small>
      ) : null}
      {choiceError ? <span className="tm-field-error">{choiceError}</span> : null}
      {help ? <small>{help}</small> : null}
    </div>
  );
}

// ---- Selector de varios (TeamRecordSelector) ----

type SelectorRow = { id: number; name: string; description: string | null; protected: boolean };

export function RecordSelector({
  kind,
  title,
  hint,
  selectedIds,
  onChange,
}: {
  kind: "members" | "groups";
  title?: string;
  hint?: string;
  selectedIds: Set<number>;
  onChange: (value: Set<number>) => void;
}) {
  const ui = useTeam();
  const T = useT();
  const api = useApi();
  const { busy, error, run, accept, working } = useOperation();
  const [selected, setSelected] = useState(() => new Set(selectedIds));
  const [rows, setRows] = useState<SelectorRow[] | null>(null);
  const [search, setSearch] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);
  const [total, setTotal] = useState(0);
  const page = useRef(1);
  const box = useRef<SearchHandle>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const state = useRef({ search, onlySelected, selected, rows });
  useEffect(() => {
    state.current = { search, onlySelected, selected, rows };
  });

  const fetchPage = async (target: number, size: number, ids: number[] | null): Promise<SelectorRow[]> => {
    const query = { search: state.current.search.trim(), status: "ACTIVE", page: target, size, ids };
    if (kind === "groups") {
      const result = await api.post<Page<Group>>("groups/search", query);
      if (!accept(result) || !result.value) return [];
      if (ids === null || state.current.onlySelected) setTotal(result.value.total);
      return result.value.items.map((group) => ({
        id: group.groupId,
        name: group.name,
        description: membersText(ui, group.memberCount),
        protected: false,
      }));
    }
    const result = await api.post<Page<Member>>("members/search", query);
    if (!accept(result) || !result.value) return [];
    if (ids === null || state.current.onlySelected) setTotal(result.value.total);
    return result.value.items.map((member) => ({
      id: member.userId,
      name: member.name,
      description: member.email,
      protected: member.userId === ui.userId,
    }));
  };
  /** Primera página: con la lista completa, las elegidas arriba; luego el resto en orden alfabético. */
  const reload = () =>
    run(async () => {
      page.current = 1;
      const { onlySelected: only, search: term, selected: chosen } = state.current;
      const loaded: SelectorRow[] = [];
      if (!only && term.length === 0 && chosen.size > 0) loaded.push(...(await fetchPage(1, 100, [...chosen])));
      const first = await fetchPage(1, 25, only ? [...chosen] : null);
      loaded.push(...first.filter((row) => loaded.every((pinned) => pinned.id !== row.id)));
      setRows(loaded);
    });
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  });
  useEffect(() => {
    void reloadRef.current();
  }, []);
  // C17: la página siguiente cuando el centinela entra en vista (faltan unas 10 filas).
  useEffect(() => {
    const element = sentinel.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || working.current) return;
        void run(async () => {
          page.current++;
          const next = await fetchPage(
            page.current,
            25,
            state.current.onlySelected ? [...state.current.selected] : null,
          );
          setRows((current) => [
            ...(current ?? []),
            ...next.filter((row) => (current ?? []).every((item) => item.id !== row.id)),
          ]);
        });
      },
      { rootMargin: "0px 0px 560px 0px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  });
  const toggle = (id: number) => {
    const next = new Set(selected);
    if (!next.delete(id)) next.add(id);
    setSelected(next);
    onChange(new Set(next));
  };
  const searchFor = (value: string) => {
    state.current.search = value;
    setSearch(value);
    void reload();
  };
  const toggleOnly = () => {
    state.current.onlySelected = !onlySelected;
    setOnlySelected(!onlySelected);
    void reload();
  };
  void busy;
  return (
    <div className="tm-selector" data-part="selector">
      <div className="tm-selector-head">
        {title ? <strong>{title}</strong> : null}
        <span className="tm-count">
          {formatText(T(selected.size === 1 ? "CFG1_R1_SELECTED_ONE" : "CFG1_R1_SELECTED_MANY"), selected.size)}
        </span>
      </div>
      {hint ? <p className="tm-hint">{hint}</p> : null}
      {total > 6 || search.length > 0 || onlySelected ? (
        <div className="tm-toolbar">
          <TeamSearch
            ref={box}
            value={search}
            onChange={searchFor}
            placeholder={T(kind === "groups" ? "CFG1_SEARCH" : "CFG1_SEARCH_MEMBER")}
            clearLabel={T("CFG1_R1_CLEAR_SEARCH")}
          />
          <div className="tm-pills" role="group" aria-label={T("CFG1_SELECTED_ONLY")}>
            <button
              type="button"
              className="tm-pill"
              aria-pressed={onlySelected ? "true" : "false"}
              onClick={toggleOnly}
            >
              {T("CFG1_SELECTED_ONLY")}
            </button>
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="tm-notice error" role="alert">
          {error}{" "}
          <button type="button" className="tm-link" onClick={() => void reload()}>
            {T("CFG1_RETRY")}
          </button>
        </div>
      ) : null}
      {rows === null ? (
        <div className="tm-more">
          <TeamLoader />
        </div>
      ) : rows.length === 0 ? (
        search.length > 0 ? (
          <TeamEmpty
            title={T("CFG1_R1_NO_RESULTS")}
            line={formatText(T(kind === "groups" ? "CFG1_R1_NO_GROUPS_WITH" : "CFG1_R1_NO_MEMBERS_WITH"), search)}
            actionLabel={T("CFG1_R1_CLEAR_SEARCH")}
            onAction={() => box.current?.clear()}
          />
        ) : onlySelected ? (
          <TeamEmpty title={T("CFG1_R1_NONE_SELECTED")} actionLabel={T("CFG1_R1_SHOW_ALL")} onAction={toggleOnly} />
        ) : (
          <TeamEmpty title={T(kind === "groups" ? "CFG1_R1_NO_GROUPS" : "CFG1_R1_NO_MEMBERS")} />
        )
      ) : (
        <>
          <div role="group" aria-label={title}>
            {rows.map((row) => {
              const chosen = selected.has(row.id);
              return (
                <button
                  key={row.id}
                  type="button"
                  role="checkbox"
                  className="tm-check-row"
                  aria-checked={chosen ? "true" : "false"}
                  disabled={row.protected}
                  title={row.protected ? T("CFG1_R1_SELF_PROTECTED") : undefined}
                  onClick={() => toggle(row.id)}
                >
                  <span className="tm-box" aria-hidden="true">
                    {chosen ? <ChatIcon name="check" /> : null}
                  </span>
                  {kind === "groups" ? (
                    <span className="tm-row-icon" aria-hidden="true">
                      <ChatIcon name="users" />
                    </span>
                  ) : (
                    <Avatar name={row.name} identity={row.id} className="tm-avatar small" />
                  )}
                  <span className="tm-row-text">
                    <span className="tm-line1">
                      <strong>{row.name}</strong>
                    </span>
                    {row.description ? <span className="tm-line2">{row.description}</span> : null}
                  </span>
                </button>
              );
            })}
          </div>
          {rows.length < total ? (
            <div className="tm-more tm-sentinel" ref={sentinel}>
              <TeamLoader withText />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
